<template>
  <div class="min-h-full flex flex-col items-center">
    <div
      class="self-stretch h-50 flex justify-center align-center p-12 transition-colors duration-300 ease-in-out-quart"
      :style="{ backgroundColor: seller.theme.initialLoading ?  '' : seller.theme.primaryColor }"
    >
      <Transition
        enter-class="transition-opacity opacity-0"
        enter-active-class="duration-200 ease-in-out-quart"
        enter-to-class="transition-opacity opacity-100 delay-300"
        leave-class="transition-opacity opacity-100"
        leave-active-class="duration-200 ease-in-out-quart"
        leave-to-class="transition-opacity opacity-0"
      >
        <img
          v-show="!seller.theme.initialLoading && seller.theme.logoLoaded"
          :src="seller.theme.logoURL"
          alt="Logo"
          class="max-w-full object-contain"
          @load="seller.theme.logoLoaded = true"
        >
      </Transition>
    </div>
    <Transition
      name="transition-fade-in-fast"
      mode="out-in"
    >
      <div
        v-if="!initialLoading && !Object.keys(vehicle).length"
        class="flex-grow flex flex-col items-center justify-center"
      >
        <IconFA
          :icon="['fad', 'car-mechanic']"
          class="text-5xl text-danger mb-20"
        />
        <h2 class="font-medium text-2xl text-danger leading-tight text-center mb-30 px-20">
          {{ $t('error.loading') }}
        </h2>
        <button
          class="button button-primary-on-white button-filled button-lg"
          @click="reloadWindow"
        >
          <IconFA
            :icon="['far', 'redo-alt']"
            class="mr-15"
          />
          {{ $t('actions.reload_page') }}
        </button>
      </div>
      <div
        v-else
        class="flex-grow w-full max-w-1224"
      >
        <VehicleDetails
          :locale="$route.params.locale"
          :id-document="vehicleIdDocumentURL"
          :user-profile="seller.publicProfile"
          :type="vehicle.type"
          :vehicle-filters="vehicleFilters"
          :vehicle-data="vehicle.data"
          :vehicle-images="vehicleImageURLs"
          :valid-until="validUntilTime"
          :vehicle="vehicle"
          class="flex-grow"
        />
      </div>
    </Transition>
  </div>
</template>


<script>
import LogRocket from 'logrocket';
import { APICollectAnalytics } from '@/api';
import { FBStorageRef } from '@/helpers/firebase/services';
import {
  getSellerPublicProfile, getVehicle, getVehicleImages, getVehicleFiltersOfType,
} from '@/helpers/firebase/firestoreRefs';
import vehicleTypes from '@/helpers/vehicleTypes';
import { vehicleImageIdsOrder } from '@/config/vehicleImagesConfig';
import VehicleDetails from '@/components/vehicle/VehicleDetails';


const autoDropLogoURL = '/images/autodrop/logo.svg';
const defaultSellerPublicProfile = {
  allowBidDeletion: false,
  allowBidModification: false,
  brand: {
    color: '',
    logoStoragePath: '',
  },
  name: '',
  phoneNumber: '',
  defaultBuyerGroupId: '',
};


export default {
  props: {
    vehicleId: {
      type: String,
      default: '',
    },
  },
  components: {
    VehicleDetails,
  },
  data: () => ({
    initialLoading: true,
    seller: {
      theme: {
        initialLoading: true,
        logoURL: autoDropLogoURL, // Default logo
        logoLoaded: false,
        primaryColor: '#363636',
      },
      publicProfile: {
        initialLoading: true,
        ...defaultSellerPublicProfile,
        brand: { ...defaultSellerPublicProfile.brand },
      },
    },
    sellerId: '',
    validUntilTime: null,
    vehicle: {},
    imageURLs: [], // Only for vehicles which have photoStoragePaths and thumbnailStoragePaths only
    idCardDocumentURL: '', // Only for vehicles which have idCardDocumentStoragePath
    vehicleImages: {},
    vehicleFilters: {},
    unsubSellerPublicProfile: null,
    unsubVehicle: null,
    unsubVehicleImages: null, // Since V. 1.4.0, for trucks only
    unsubVehicleFilters: null,
  }),
  created() {
    // Getting vehicle
    this.unsubVehicle = getVehicle(this.vehicleId)
      .onSnapshot((vehicleDoc) => {
        const docData = vehicleDoc.data();

        if (!vehicleDoc.exists || !Object.keys(docData).length) {
          this.initialLoading = false;
          return;
        }

        // Setting sellerId and status
        if (docData.seller && docData.seller.id) {
          this.sellerId = docData.seller.id;
        } else if (docData.sellerId) { // docData.sellerId is deprecated
          this.sellerId = docData.sellerId;
        }

        // Setting seller color/logo
        if (docData.brand) {
          if (docData.brand.color) {
            this.seller.theme.primaryColor = docData.brand.color;
          }

          if (docData.brand.logoStoragePath) {
            FBStorageRef
              .child(docData.brand.logoStoragePath)
              .getDownloadURL()
              .then((imageURL) => {
                this.seller.theme.logoURL = imageURL;
              })
              .catch((error) => {
                this.seller.theme.logoURL = autoDropLogoURL;
                console.error(error);
              })
              .finally(() => {
                this.seller.theme.initialLoading = false;
              });
          } else {
            this.seller.theme.initialLoading = false;
            this.seller.theme.logoURL = autoDropLogoURL;
          }
        } else {
          console.info('No brand info on the vehicle, using defaults');
          this.seller.theme.initialLoading = false;
          this.seller.theme.logoURL = autoDropLogoURL;
        }

        // Setting validUntilTime formatted string (By default currentTime +1 day)
        if (!docData.validUntil || !docData.validUntil.constructor
          || docData.validUntil.constructor.name !== 't') {
          const currentTime = new Date();
          currentTime.setDate(currentTime.getDate() + 1);
          this.validUntilTime = currentTime.toLocaleDateString(this.$route.params.locale);
        } else {
          this.validUntilTime = docData.validUntil.toDate().toLocaleDateString(this.$route.params.locale);
        }

        if (!docData.data) {
          return;
        }

        // Setting vehicle data
        this.$set(this, 'vehicle', docData);

        // Setting imageURLs, only for vehicles which have photoStoragePaths or thumbnailStoragePaths
        let vehicleImageStoragePathKey = 'photoStoragePaths';
        if (docData.data.thumbnailStoragePaths) vehicleImageStoragePathKey = 'thumbnailStoragePaths';

        if (docData.data[vehicleImageStoragePathKey]) {
          // Reset imageURLs if previously set
          this.imageURLs = [];

          docData.data[vehicleImageStoragePathKey].forEach((imageStoragePath, index) => {
            FBStorageRef
              .child(imageStoragePath)
              .getDownloadURL()
              .then((url) => {
                this.$set(this.imageURLs, index, url);
              })
              .catch((error) => {
                console.error(error);
              });
          });
        }

        if (docData.data.idCardDocumentStoragePath) {
          FBStorageRef
            .child(docData.data.idCardDocumentStoragePath)
            .getDownloadURL()
            .then((documentURL) => {
              this.idCardDocumentURL = documentURL;
            })
            .catch((error) => {
              this.idCardDocumentURL = '';
              console.error(error);
            });
        }

        this.initialLoading = false;
      }, (error) => {
        this.initialLoading = false;

        console.error(error);
      });
  },
  mounted() {
    setTimeout(() => {
      this.collectAnalytics();
    }, 3000);
  },
  watch: {
    'vehicle.type': {
      immediate: true,
      handler(value) {
        if (typeof this.unsubVehicleFilters === 'function') this.unsubVehicleFilters();

        if (!value) return;

        // Subscribing to the vehicle filters
        this.unsubVehicleFilters = getVehicleFiltersOfType(value)
          .onSnapshot((snapshot) => {
            this.$set(this, 'vehicleFilters', {});

            snapshot.forEach((doc) => {
              this.$set(this.vehicleFilters, doc.id, doc.data().options);
            });
          }, (error) => {
            console.error(error);
          });

        // Subscribing to the vehicle images (Trucks only)
        if (value === vehicleTypes.TRUCK) {
          this.unsubVehicleImages = getVehicleImages(this.vehicleId)
            .onSnapshot((snapshot) => {
              snapshot.forEach((doc) => {
                const imageData = doc.data();

                if (!imageData?.originalStoragePath) return;

                // Load thumbnail or original image
                FBStorageRef.child(imageData.originalStoragePath)
                  .getDownloadURL()
                  .then((imageURL) => {
                    this.$set(this.vehicleImages, doc.id, imageURL);
                  })
                  .catch((error) => {
                    this.$set(this.vehicleImages, doc.id, '');
                    console.error(error);
                  });
              });
            }, (error) => {
              console.error(error);
            });
        }
      },
    },
    sellerId: {
      immediate: true,
      handler(value) {
        if (typeof this.unsubSellerPublicProfile === 'function') this.unsubSellerPublicProfile();

        if (!value) return;

        this.sellerPublicProfile = {
          initialLoading: true,
          allowBidDeletion: false,
          allowBidModification: false,
          brand: {
            color: '',
            logoStoragePath: '',
          },
          name: '',
          phoneNumber: '',
          defaultBuyerGroupId: '',
        };

        // Getting User (Seller) public profile
        this.unsubSellerPublicProfile = getSellerPublicProfile(value)
          .onSnapshot((snapshot) => {
            this.$set(this.seller, 'publicProfile', snapshot.data());
          }, (error) => {
            console.error(error);
          });
      },
    },
  },
  computed: {
    vehicleIdDocumentURL() {
      if (this.vehicle.type === vehicleTypes.TRUCK) {
        return this.vehicleImages.idDocument;
      }
      return this.idCardDocumentURL;
    },
    vehicleImageURLs() {
      if (this.vehicle.type === vehicleTypes.TRUCK) {
        return vehicleImageIdsOrder.map((imageType) => this.vehicleImages[imageType]);
      }
      return this.imageURLs;
    },
  },
  methods: {
    collectAnalytics() {
      APICollectAnalytics({
        version: process.env.VUE_APP_VERSION,
        buyerId: this.$route.params.buyerId,
        vehicleId: this.$route.params.vehicleId,
        hitType: 'pageview',
        hostname: window.location,
        pageURL: window.location.href,
        pageTitle: document.title,
      })
        .then((response) => {
          if (response.status === 200) {
            if (process.env.VUE_APP_MODE === 'development' || process.env.NODE_ENV === 'development') {
              console.info('Tracked page view');
            } else {
              LogRocket.info('Tracked page view');
            }
          }
        });
    },
    reloadWindow() {
      window.reload();
    },
  },
};
</script>
